// src/pages/AddClientForm.jsx
import React, { useEffect, useState } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  Building2,
  Navigation,
  Hash,
  Save,
  X,
  AlertCircle,
  Plus,
  Users,
  ArrowLeft,
  Lock,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import toast, { Toaster } from 'react-hot-toast';

const AddClientForm = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If ?id=123 exists → edit mode
  const clientId = searchParams.get('id');
  const isEditMode = Boolean(clientId);

  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    mobileNumber: '',

    // Address Information
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    country: 'India',

    // Additional
    notes: '',

    // Login / Security
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false); // submit loading
  const [initialLoading, setInitialLoading] = useState(false); // loading client for edit
  const [errors, setErrors] = useState({}); // field-level errors only
  const getAuthToken = () =>
    localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const getAuthRole = () =>
    localStorage.getItem('authRole') || sessionStorage.getItem('authRole');
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  ];
   const logoutAndRedirect = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRole');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authRole');
    navigate('/');
  };

 

  // ----------- Load existing client in edit mode -----------
  useEffect(() => {
    if (!isEditMode) return;

    const fetchClient = async () => {
      try {
        setInitialLoading(true);
        setErrors({});

        const token = getAuthToken();
             const role = getAuthRole();
       
             // No token or not admin → send to login
             if (!token || role !== 'admin') {
               toast.error('Session expired. Please log in again.');
               logoutAndRedirect();
               return;
             } // adjust key if different

        const response = await fetch(
          `https://patrol-visit.patroll-management.nissiindia.com/api/clients/${clientId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json().catch(() => ({}));
console.log("data",data);
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('adminToken');
            sessionStorage.removeItem('adminToken');
            toast.error('Session expired. Please log in again.');
            navigate('/');
            return;
          }
          throw new Error(data.message || 'Failed to load client details');
        }

        const c = data.client || {};

        // Support both "form shape" and "list shape" keys
        setFormData((prev) => ({
          ...prev,
          fullName: c.fullName || c.name || '',
          email: c.email || '',
          mobileNumber: c.mobileNumber || c.phone || '',
          addressLine1: c.address || '',
          addressLine2: c.address_line2 || '',
          city: c.city || '',
          state: c.state || '',
          pincode: c.pincode || '',
          landmark: c.landmark || '',
          country: c.country || 'India',
          notes: c.notes || '',
          // password fields stay empty in edit mode
          password: '',
          confirmPassword: '',
        }));
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Failed to load client details.');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal Information Validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber =
        'Please enter a valid 10-digit Indian mobile number';
    }

    // Address Validation
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    }

    // Password Validation
    if (!isEditMode) {
      // Add mode → password required
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = 'Please confirm the password';
      } else if (formData.confirmPassword !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // Edit mode → password optional, but if provided, must be valid
      if (formData.password.trim()) {
        if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        }
        if (!formData.confirmPassword.trim()) {
          newErrors.confirmPassword = 'Please confirm the new password';
        } else if (formData.confirmPassword !== formData.password) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the highlighted errors.');
      return;
    }

    setLoading(true);

    try {
       const token = getAuthToken();
      const role = getAuthRole();

      // No token or not admin → send to login
      if (!token || role !== 'admin') {
        toast.error('Session expired. Please log in again.');
        logoutAndRedirect();
        return;
      } // adjust key if different

      const url = isEditMode
        ? `https://patrol-visit.patroll-management.nissiindia.com/api/clients/${clientId}`
        : 'https://patrol-visit.patroll-management.nissiindia.com/api/clients/add';

      const method = isEditMode ? 'PUT' : 'POST';

      // Don't send confirmPassword to backend
      const payload = { ...formData };
      delete payload.confirmPassword;

      // In edit mode, if password is empty, remove it so backend keeps old hash
      if (isEditMode && !payload.password.trim()) {
        delete payload.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          toast.error('Session expired. Please log in again.');
          navigate('/');
          return;
        }
        throw new Error(
          data.message ||
            (isEditMode ? 'Failed to update client' : 'Failed to add client')
        );
      }

      toast.success(
        isEditMode
          ? 'Client updated successfully'
          : 'Client added successfully'
      );

      // Navigate back after a short delay so user can see toast
      setTimeout(() => {
        navigate('/clients');
      }, 1200);
    } catch (error) {
      console.error(error);
      toast.error(
        error.message ||
          (isEditMode
            ? 'Failed to update client. Please try again.'
            : 'Failed to add client. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditMode) {
      // In edit mode, reset to last loaded values by refetching
      window.location.reload();
      return;
    }

    setFormData({
      fullName: '',
      email: '',
      mobileNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      country: 'India',
      notes: '',
      password: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  // ------------------- UI -------------------

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/clients')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Back to Clients"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
              }}
            >
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {isEditMode ? 'Edit Client' : 'Add New Client'}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {isEditMode
                  ? 'Update the client information'
                  : 'Register a new client in the Patrol Visit system'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/clients')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${theme.primary[100]}, ${theme.secondary[100]})`,
              }}
            >
              <User className="w-5 h-5" style={{ color: theme.primary[600] }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Personal Information
              </h2>
              <p className="text-xs text-slate-600">Basic details of the client</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="md:col-span-2">
              <label
                htmlFor="fullName"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter client's full name"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.fullName
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.fullName
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.fullName) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.fullName) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.email
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.email
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.email) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.email) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label
                htmlFor="mobileNumber"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  placeholder="9876543210"
                  maxLength="10"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.mobileNumber
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.mobileNumber
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.mobileNumber) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.mobileNumber) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.mobileNumber && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.mobileNumber}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Login / Password Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Login & Security
              </h2>
              <p className="text-xs text-slate-600">
                Set the client’s password for portal access
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                {isEditMode ? 'New Password' : 'Password'}{' '}
                {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    isEditMode
                      ? 'Leave blank to keep existing password'
                      : 'At least 8 characters'
                  }
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.password
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.password
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.password) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.password) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.password}
                </p>
              )}
              {isEditMode && !errors.password && (
                <p className="mt-1 text-xs text-slate-500">
                  Leave blank if you don&apos;t want to change the password.
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Confirm Password
                {(!isEditMode || formData.password.trim()) && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.confirmPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.confirmPassword
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.confirmPassword) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.confirmPassword) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Address Details</h2>
              <p className="text-xs text-slate-600">
                Complete address information
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Address Line 1 */}
            <div className="md:col-span-2">
              <label
                htmlFor="addressLine1"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Home className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="House/Flat No., Building Name, Street"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.addressLine1
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.addressLine1
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.addressLine1) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.addressLine1) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.addressLine1 && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.addressLine1}
                </p>
              )}
            </div>

            {/* Address Line 2 */}
            <div className="md:col-span-2">
              <label
                htmlFor="addressLine2"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Address Line 2{' '}
                <span className="text-slate-400 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Area, Colony, Sector (Optional)"
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all"
                  style={{
                    '--tw-ring-color': `${theme.primary[500]}33`,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500];
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }}
                />
              </div>
            </div>

            {/* City */}
            <div>
              <label
                htmlFor="city"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                City <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter city name"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.city
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.city
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.city) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.city) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.city && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.city}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label
                htmlFor="state"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all appearance-none bg-white ${
                    errors.state
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.state
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.state) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.state) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                >
                  <option value="">Select State</option>
                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.state}
                </p>
              )}
            </div>

            {/* Pincode */}
            <div>
              <label
                htmlFor="pincode"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Pincode <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  placeholder="600001"
                  maxLength="6"
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-4 outline-none transition-all ${
                    errors.pincode
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-slate-200 focus:ring-opacity-20'
                  }`}
                  style={
                    !errors.pincode
                      ? {
                          '--tw-ring-color': `${theme.primary[500]}33`,
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!errors.pincode) {
                      e.target.style.borderColor = theme.primary[500];
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.pincode) {
                      e.target.style.borderColor = 'rgb(226 232 240)';
                    }
                  }}
                />
              </div>
              {errors.pincode && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.pincode}
                </p>
              )}
            </div>

            {/* Landmark */}
            <div>
              <label
                htmlFor="landmark"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Landmark{' '}
                <span className="text-slate-400 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <Navigation className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="landmark"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleChange}
                  placeholder="Near XYZ Mall, Opposite ABC Park"
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all"
                  style={{
                    '--tw-ring-color': `${theme.primary[500]}33`,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500];
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgb(226 232 240)';
                  }}
                />
              </div>
            </div>

            {/* Country */}
            <div className="md:col-span-2">
              <label
                htmlFor="country"
                className="block text-sm font-semibold text-slate-700 mb-2"
              >
                Country
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  readOnly
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl bg-slate-50 text-slate-600 cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Notes Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Plus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Additional Information
              </h2>
              <p className="text-xs text-slate-600">
                Any special notes or instructions
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-semibold text-slate-700 mb-2"
            >
              Notes <span className="text-slate-400 text-xs">(Optional)</span>
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Add any special instructions, property details, or important notes about this client..."
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 outline-none transition-all resize-none"
              style={{
                '--tw-ring-color': `${theme.primary[500]}33`,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.primary[500];
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgb(226 232 240)';
              }}
            ></textarea>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all duration-200"
            >
              {isEditMode ? 'Reset Changes' : 'Reset Form'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                loading
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'hover:shadow-lg hover:scale-105 active:scale-100'
              }`}
              style={
                !loading
                  ? {
                      background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.secondary[600]} 100%)`,
                    }
                  : {}
              }
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>
                    {isEditMode ? 'Updating Client...' : 'Adding Client...'}
                  </span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{isEditMode ? 'Update Client' : 'Add Client'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AddClientForm;
